import { ApplicationModelStateShape } from "checkout/ts/redux/models/application";
import { PatchedCheckoutInfo } from "checkout/ts/redux/models/configuration";
import { PatchedTokenCardData, ThreeDsStatus } from "checkout/ts/redux/sdk/resources/TransactionTokens";
import { isTransactionThreeDsAddressRequired, isTransactionThreeDsRequired } from "checkout/ts/redux/utils/token";
import { STATES_BY_COUNTRY } from "common/locale/states";
import { PaymentType, TransactionTokenItem } from "univapay-node";

/**
 * Legacy support for old `address` parameter
 */
export const getRequiredFields = (
    paymentType: PaymentType,
    application: ApplicationModelStateShape,
    checkoutInfo?: PatchedCheckoutInfo,
    selectedToken?: TransactionTokenItem
) => {
    const LEGACY_REQUIRE_EMAIL = [PaymentType.ONLINE];
    const LEGACY_REQUIRE_BILLING = [PaymentType.CARD, PaymentType.PAIDY];
    const LEGACY_REQUIRE_PHONE = [PaymentType.KONBINI];
    const {
        address = false,
        requireEmail = false,
        requireBillingAddress,
        requirePhoneNumber,
        requireName = false,
        requireNameKana = false,

        shippingAddressZip,
        shippingAddressCity,
        shippingAddressCountryCode,
        shippingAddressLine1,
        shippingAddressState,
        phoneNumber: prefilledPhoneNumber,
    } = application.params.params;

    const isCustomerIdTokenAuthorized =
        !!selectedToken && (selectedToken?.data as PatchedTokenCardData)?.threeDs?.status === ThreeDsStatus.SUCCESSFUL;

    const requireThreeDsAdress =
        !isCustomerIdTokenAuthorized && isTransactionThreeDsAddressRequired(application.params.params, checkoutInfo);

    const requireThreeDsPhone =
        !isCustomerIdTokenAuthorized && isTransactionThreeDsRequired(application.params.params, checkoutInfo);

    const availableStates = shippingAddressCountryCode
        ? STATES_BY_COUNTRY[shippingAddressCountryCode.toLocaleUpperCase()]
        : undefined;
    const addressState =
        availableStates && shippingAddressState && requireThreeDsAdress
            ? availableStates.find(
                  (state) =>
                      state.name.toLowerCase() === shippingAddressState.toLowerCase() ||
                      (state.label && shippingAddressState.toLowerCase().includes(state.label.toLowerCase()))
              )?.name
            : shippingAddressState;

    const hasPrefilledAddress =
        !!shippingAddressCity &&
        !!shippingAddressCountryCode &&
        !!shippingAddressLine1 &&
        !!addressState &&
        !!shippingAddressZip;

    const forceEmail = paymentType === PaymentType.CARD || paymentType === PaymentType.BANK_TRANSFER;
    const forceBilling = paymentType === PaymentType.PAIDY && (!shippingAddressZip || !shippingAddressCity);
    const forceThreeDsAddress =
        paymentType === PaymentType.CARD &&
        requireThreeDsAdress &&
        (requireBillingAddress !== false || !hasPrefilledAddress);
    const forceThreeDsPhoneNumber =
        paymentType === PaymentType.CARD &&
        requireThreeDsPhone &&
        (requirePhoneNumber !== false || !prefilledPhoneNumber);
    const forceName =
        paymentType === PaymentType.BANK_TRANSFER ||
        paymentType === PaymentType.PAIDY ||
        paymentType === PaymentType.KONBINI;
    const forceNameKana = paymentType === PaymentType.PAIDY;

    return {
        requireEmail:
            application.invalidFields.email?.showField ||
            requireEmail ||
            (address && LEGACY_REQUIRE_EMAIL.includes(paymentType)) ||
            forceEmail,
        requirePhoneNumber:
            application.invalidFields.phoneNumber?.showField ||
            requirePhoneNumber ||
            (address && LEGACY_REQUIRE_PHONE.includes(paymentType)),
        forceThreeDsPhoneNumber,
        requireBillingAddress:
            hasInvalidAddressField(application.invalidFields) ||
            requireBillingAddress ||
            (address && LEGACY_REQUIRE_BILLING.includes(paymentType)) ||
            forceBilling,
        forceThreeDsAddress,
        requireName: application.invalidFields.name?.showField || requireName || forceName,
        requireNameKana: application.invalidFields.nameKana?.showField || requireNameKana || forceNameKana,
    };
};

export const normalizeState = (state: string) => {
    if (!state) {
        return state;
    }

    const replaceAll = (text: string, letter: string, replace: string) => text?.split(letter).join(replace);
    const replacements = [["ô", "o"]];

    return replacements.reduce((acc, [letter, replace]) => replaceAll(acc, letter, replace), state);
};

export const hasInvalidAddressField = (invalidFields: Record<string, { showField: boolean; error: string }>) =>
    ["line1", "line2", "state", "city", "country", "zip"].some((addressKey) => invalidFields[addressKey]?.showField);
